import { BadgeCheck, SearchX } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { useVerificacaoPublicaControllerVerificar } from '@/api/generated/documentos/documentos';
import { EstadoCarregando, EstadoVazio } from '@/shared/components/estados';
import { LogoNohra } from '@/shared/components/LogoNohra';
import { Card, CardContent } from '@/shared/ui/card';

/**
 * A página que o QR da Receita de Controle Especial abre ("esta prescrição pode
 * ser verificada em").
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  Quem chega aqui é o FARMACÊUTICO, com a receita na mão: sem conta, quase
 *  sempre pelo celular. A tela responde uma pergunta só — "esta receita existe
 *  e é isto mesmo?" — com o que ele confronta com o papel: quem prescreveu,
 *  quando, o número do SNCR e os medicamentos. Nada do tutor (o backend nem
 *  manda), e nenhum caminho para dentro do sistema.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function VerificarReceitaPage() {
  const { codigo = '' } = useParams<{ codigo: string }>();

  const { data, isPending, isError } = useVerificacaoPublicaControllerVerificar(codigo, {
    query: {
      enabled: Boolean(codigo),
      // Código errado não melhora com insistência, e cada tentativa consome a
      // cota do throttler da rota pública.
      retry: false,
    },
  });

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      <header className="border-b bg-surface-raised">
        <div className="mx-auto max-w-2xl px-4 py-3 flex justify-center">
          <LogoNohra tamanho="sm" />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-2xl p-4 space-y-4">
        {isPending && <EstadoCarregando rotulo="Verificando a receita..." />}

        {isError && (
          <EstadoVazio
            icone={SearchX}
            titulo="Receita não encontrada"
            descricao="Confira o código impresso no rodapé da receita. Se estiver correto, a receita não foi emitida por esta plataforma."
          />
        )}

        {data && (
          <Card>
            <CardContent className="p-5 space-y-5">
              <div className="flex items-start gap-3">
                <BadgeCheck className="w-7 h-7 text-emerald-600 shrink-0" aria-hidden />
                <div>
                  <h1 className="text-lg font-semibold">Receita de controle especial autêntica</h1>
                  <p className="text-sm text-muted-foreground">
                    Emitida pela plataforma NOHRA Clinic
                    {data.assinadoEm
                      ? ` em ${new Date(data.assinadoEm).toLocaleString('pt-BR', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}`
                      : ''}
                    .
                  </p>
                </div>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <Dado rotulo="N.º (SNCR)" valor={data.numeroSncr ?? '—'} />
                <Dado rotulo="Prescritor" valor={[data.prescritorNome, data.prescritorCrmv].filter(Boolean).join(' — ')} />
                <Dado rotulo="Animal" valor={`${data.animalNome} (${data.animalEspecie.toLowerCase()})`} />
                <Dado
                  rotulo="Assinatura digital ICP-Brasil"
                  valor={data.assinaturaDigital ? 'Sim — valide o PDF em validar.iti.gov.br' : 'Não'}
                />
              </dl>

              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-brand uppercase tracking-wide">Prescrição</h2>
                <ul className="space-y-2">
                  {data.itens.map((item, indice) => (
                    <li key={indice} className="rounded-md border px-3 py-2 text-sm">
                      <p className="font-medium">
                        {[item.medicamento, item.concentracao].filter(Boolean).join(' ')}
                      </p>
                      <p className="text-muted-foreground">
                        {[item.formaFarmaceutica, item.quantidade].filter(Boolean).join(' — ') || '—'}
                      </p>
                      {item.posologia && <p className="text-muted-foreground">{item.posologia}</p>}
                    </li>
                  ))}
                </ul>
              </section>

              <p className="text-xs text-muted-foreground">
                Registre a utilização desta prescrição em{' '}
                <a
                  href="https://sncr.anvisa.gov.br"
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium underline"
                >
                  sncr.anvisa.gov.br
                </a>
                .
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function Dado({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{rotulo}</dt>
      <dd className="font-medium">{valor}</dd>
    </div>
  );
}
